import { getCompanyInfo } from "@/api/endpoints/company-info";
import useDebounce from "@/hooks/useDebounce";
import { FieldDescription } from "@components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@components/ui/input-group";
import { Spinner } from "@components/ui/spinner";
import { Trans } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { CheckIcon, SearchIcon, XIcon } from "lucide-react";
import { forwardRef, useEffect } from "react";

type EntitySearchResult = {
  url: string;
  companyName: string;
  fullCompanyName?: string;
  companyDescription: string;
};

type EntitySearchProps = {
  onSuccess: (result: EntitySearchResult) => void;
  onError: (url?: string, error?: unknown) => void;
} & Omit<React.ComponentProps<"input">, "onError"> & { value: string };

function EntitySearch({ onSuccess, onError, ...rest }: EntitySearchProps) {
  // Local state
  const debouncedUrl = useDebounce(rest.value, 1200);

  // Server state
  const {
    mutate,
    data,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: getCompanyInfo,
    mutationKey: ["company-info", debouncedUrl],
    retry: false,
    onSuccess: (res) => {
      if (!res.ok) {
        throw new Error(res.data?.detail.message);
      }

      onSuccess({
        url: debouncedUrl,
        companyName: res.data.company_name,
        fullCompanyName: res.data.full_company_name,
        companyDescription: res.data.company_description,
      });
    },
    onError: (e) => {
      onError(debouncedUrl, e.message);
    },
  });

  useEffect(() => {
    if (debouncedUrl) {
      mutate(debouncedUrl);
    }
  }, [debouncedUrl, mutate]);

  return (
    <>
      <InputGroup>
        <InputGroupAddon align="inline-start">
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          autoFocus
          disabled={isLoading}
          placeholder="www.allears.ai"
          aria-invalid={Boolean(error)}
          {...rest}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        />
        {isLoading ? (
          <InputGroupAddon
            align="inline-end"
            data-testid="entity-search-loading"
          >
            <Spinner />
          </InputGroupAddon>
        ) : data ? (
          <InputGroupAddon
            align="inline-end"
            data-testid="entity-search-success"
          >
            <CheckIcon className="stroke-green-600" />
          </InputGroupAddon>
        ) : error ? (
          <InputGroupAddon align="inline-end" data-testid="entity-search-error">
            <XIcon className="stroke-red-600" />
          </InputGroupAddon>
        ) : null}
      </InputGroup>

      {error ? (
        <FieldDescription>
          <Trans>
            Det ser tyvärr ut som att vi inte hittar något på detta, vänligen ge
            oss lite mer information.
          </Trans>
        </FieldDescription>
      ) : (
        !data && (
          <FieldDescription>
            <Trans>
              Vi använder detta för att sätta upp din första bevakning
            </Trans>
          </FieldDescription>
        )
      )}
    </>
  );
}

export default forwardRef(EntitySearch);
